import {cs}          from "../react/chain-services";
import {fragments}   from "../react/fragments";
import {Invoke}      from "../react/invoke";
import {OnUnmounted} from "../react/on-unmounted";
import {Static2}     from "../react/static-2";
import {UseState}    from "../react/use-state";
import {equalDeep}   from "../utils/objects";

export const DomSizeObserver = (
    {
        translate=v=>v,
        getDefaultValue=null,
        defaultValue={width: 1024, height: 768} as any,
        compare=equalDeep,
        next,
    }
) => cs(
    ["size", (_, next) => UseState({next})],
    ["dom", (_, next) => UseState({next})],
    ["nativeObserver", (_, next) => Static2({next})],
    ({size, nativeObserver, dom}) => {
        const value = size.value ? (
            size.value.translated
        ) : getDefaultValue ? (
            getDefaultValue()
        ) : (
            defaultValue
        );

        return fragments(
            dom.value && (
                Invoke({
                    props: {size, value},
                    action: ({getLatestProps}) => {
                        nativeObserver.set(createNativeObserver({
                            dom: dom.value,
                            onResize: (s1) => {
                                const {size, value} = getLatestProps();
                                const newTranslated = translate(s1);
                                if (!size.value || !compare(newTranslated, value)) {
                                    size.onChange({translated: newTranslated});
                                }
                            },
                        }));
                    },
                })
            ),
            next({
                ref: (dom1) => {
                    if (!dom1 || dom.value) {
                        return;
                    }
                    dom.onChange(dom1);
                },
                value,
            }),
            OnUnmounted({
                action: () => nativeObserver.get()?.destroy(),
            }),
        );
    },
);

const createIntervalObserver = ({dom, onResize}) => {
    let lastReported = null;
    const translate = (rect) => ({
        width: rect.width,
        height: rect.height,
    })

    const interval = setInterval(
        () => {
            const rect = dom.getBoundingClientRect();
            const v1 = translate(rect);
            if (lastReported == null || !equalDeep(v1, lastReported)) {
                lastReported = v1;
                onResize(v1);
            }
        },
        1000,
    )

    return {
        destroy: () => {
            clearInterval(interval);
        },
    };
};

const createBrowserObserver = ({dom, onResize}) => {
    let lastReported = null;
    const translate = (rect) => ({
        width: rect.width,
        height: rect.height,
    })

    const resizeObserver = window["ResizeObserver"] ? new window["ResizeObserver"](() => {
        const rect = dom.getBoundingClientRect();
        const v1 = translate(rect);
        if (lastReported == null || !equalDeep(v1, lastReported)) {
            lastReported = v1;
            onResize(v1);
        }
    }) : null;

    resizeObserver?.observe(dom);

    return {
        destroy: () => {
            resizeObserver?.unobserve(dom);
        },
    };
};

const createNativeObserver = (() => {
    if (typeof window["ResizeObserver"] !== "undefined") {
        return createBrowserObserver;
    } else {
        return createIntervalObserver;
    }
})();
